<template>
  <div
    class="ops-ping"
    :class="{ 'ops-ping--hi': highlight }"
    :style="boxStyle"
    :data-to="ping.to"
    data-test="ops-ping"
  >
    <span class="ops-ping__edge" :style="{ background: edgeColor }" aria-hidden="true"></span>
    <div class="ops-ping__top">
      <span class="ops-ping__op" data-test="ops-ping-op">{{ ping.op }}</span>
      <span class="ops-ping__at">{{ ping.at }}</span>
    </div>
    <div class="ops-ping__msg" data-test="ops-ping-msg">{{ ping.msg }}</div>
    <div class="ops-ping__from">{{ ping.from }}</div>
    <div v-if="ping.elapsed.text" class="ops-ping__elapsed" :style="chipStyle" data-test="ops-ping-elapsed">
      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>{{ ping.elapsed.text }}
    </div>
    <button type="button" class="ops-ping__clear" data-test="ops-ping-clear" @click.stop="$emit('clear', ping)">Clear</button>
  </div>
</template>

<script>
/**
 * One ping in the Alerts column (plan "Design review"): the recipient's colour on the left edge, an elapsed chip
 * whose tone follows the wait threshold, and Clear. The row comes from boardText.js buildPings; clearing is the
 * page's (the classic _deleteAlert, by the ping's `index` in the document's alerts[]).
 */
import { tintHex } from '@/utils/practiceOps/boardText';
import { isTooLight } from '@/utils/scheduleView/provColor';
import { WALL_THEMES } from '@/utils/practiceOps/viewPref';

const CHIP_TONES = {
  n: { bg: '#ffffff', fg: '#3a4240', edge: '#dfe4e3' },
  a: { bg: '#fef6e7', fg: '#b45309', edge: '#f59e0b' },
  r: { bg: '#fdecec', fg: '#c2333a', edge: '#e5484d' },
};

export default {
  name: 'OpsPingCard',

  props: {
    /** A row of boardText.js buildPings().pings. */
    ping: { type: Object, required: true },
    /** True for a moment after a recipient legend chip jumped to it. */
    highlight: { type: Boolean, default: false },
  },

  computed: {
    /** The recipient's colour, or null when there is none to draw (everyone / no setting). */
    color() {
      const c = this.ping.color;
      return typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c) ? c : null;
    },

    edgeColor() {
      return this.color && !isTooLight(this.color) ? this.color : WALL_THEMES.light.stripeFallback;
    },

    boxStyle() {
      const c = this.color;
      return {
        background: c ? tintHex(c, 0.2, '#ffffff') : '#ffffff',
        borderColor: this.highlight ? WALL_THEMES.light.accent : (c ? tintHex(this.edgeColor, 0.45, '#ffffff') : '#dfe4e3'),
      };
    },

    chipStyle() {
      const t = CHIP_TONES[this.ping.elapsed.tone] || CHIP_TONES.n;
      return { background: t.bg, color: t.fg, borderColor: t.edge };
    },
  },
};
</script>

<style lang="scss" scoped>
.ops-ping {
  position: relative;
  flex: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 11px 12px 11px 16px;
  border: 1.5px solid #dfe4e3;
  border-radius: 12px;
  transition: border-color 0.2s ease;
}

.ops-ping__edge {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
}

.ops-ping__top {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ops-ping__op {
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  font-weight: 800;
}

.ops-ping__at {
  font-size: 12px;
  color: #707876;
  font-variant-numeric: tabular-nums;
}

.ops-ping__msg {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.ops-ping__from {
  font-size: 12.5px;
  color: #3a4240;
}

.ops-ping__elapsed {
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 5px;
  height: 24px;
  padding: 0 9px;
  margin-top: 2px;
  border: 1px solid;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;

  svg { display: block; flex: none; }
}

.ops-ping__clear {
  margin-top: 6px;
  height: 34px;
  border: 1px solid #dfe4e3;
  border-radius: 8px;
  background: #ffffff;
  color: #0c0f0e;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover { background: #eff3f2; }
}
</style>
